import React, { useState, useEffect } from 'react';
import { 
  Plus, Play, Pause, Edit2, Trash2, Workflow as WorkflowIcon,
  Zap, MessageSquare, UserPlus, Mail, Clock, RefreshCw, AlertCircle
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogTrigger,
  DialogFooter
} from '../../components/ui/dialog';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../../components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { useAuth, API } from '../../App';
import { toast } from 'sonner';
import axios from 'axios';

import ChannelSelector from '../../components/ChannelSelector';

const INTENTS = [
  { value: 'sales', label: 'Sales' },
  { value: 'support', label: 'Support' },
  { value: 'booking', label: 'Booking' },
  { value: 'complaint', label: 'Complaint' },
  { value: 'inquiry', label: 'Inquiry' },
  { value: 'greeting', label: 'Greeting' },
  { value: 'other', label: 'Other' }
];

const ASSIGNMENT_TYPES = [
  { value: 'owner', label: 'Owner' },
  { value: 'admin', label: 'Any Admin' },
  { value: 'specific', label: 'Specific Operator' }
];

const WorkflowsPage = () => {
  const { token, user } = useAuth();
  const [workflows, setWorkflows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingWorkflow, setEditingWorkflow] = useState(null);
  const [operators, setOperators] = useState([]);
  const [validationError, setValidationError] = useState('');
  const [selectedChannelId, setSelectedChannelId] = useState(null);
  
  // Form state
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [triggerType, setTriggerType] = useState('new_message');
  const [triggerIntent, setTriggerIntent] = useState('');
  const [slaDuration, setSlaDuration] = useState(5);
  const [slaUnit, setSlaUnit] = useState('minutes');
  const [actionType, setActionType] = useState('send_message');
  const [actionConfig, setActionConfig] = useState('');
  const [assignmentType, setAssignmentType] = useState('owner');
  const [selectedOperator, setSelectedOperator] = useState('');

  useEffect(() => {
    fetchWorkflowConfig();
  }, []);

  useEffect(() => {
    if (selectedChannelId) {
      setLoading(true);
      fetchWorkflows();
    }
  }, [selectedChannelId]);

  const fetchWorkflows = async () => {
    try {
      let url;
      if (selectedChannelId && selectedChannelId !== 'all') {
        url = `${API}/channels/${selectedChannelId}/workflows`;
      } else {
        url = `${API}/workflows`;
      }
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWorkflows(response.data);
    } catch (error) {
      console.error('Failed to fetch workflows:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchWorkflowConfig = async () => {
    try {
      const response = await axios.get(`${API}/workflows/config`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOperators(response.data.operators || []);
    } catch (error) {
      console.error('Failed to fetch workflow config:', error);
    }
  };

  const resetForm = () => {
    setName('');
    setDescription('');
    setTriggerType('new_message');
    setTriggerIntent('');
    setSlaDuration(5);
    setSlaUnit('minutes');
    setActionType('send_message');
    setActionConfig('');
    setAssignmentType('owner');
    setSelectedOperator('');
    setEditingWorkflow(null);
    setValidationError('');
  };

  const openCreateDialog = () => {
    resetForm();
    setIsDialogOpen(true);
  };

  const openEditDialog = (workflow) => {
    setEditingWorkflow(workflow);
    setName(workflow.name);
    setDescription(workflow.description || '');
    setTriggerType(workflow.trigger?.type || 'new_message');
    setTriggerIntent(workflow.trigger?.config?.intent || '');
    setSlaDuration(workflow.trigger?.config?.sla_duration || 5);
    setSlaUnit(workflow.trigger?.config?.sla_unit || 'minutes');
    setActionType(workflow.actions?.[0]?.type || 'send_message');
    setActionConfig(workflow.actions?.[0]?.config?.message || '');
    setAssignmentType(workflow.actions?.[0]?.config?.assignment_type || 'owner');
    setSelectedOperator(workflow.actions?.[0]?.config?.operator_id || '');
    setValidationError('');
    setIsDialogOpen(true);
  };

  const validateForm = () => {
    // Validate intent for intent_detected trigger
    if (triggerType === 'intent_detected' && !triggerIntent) {
      setValidationError('Please select an intent for the Intent Detected trigger');
      return false;
    }
    
    // Validate SLA duration for SLA triggers
    if ((triggerType === 'no_response_sla' || triggerType === 'time_based') && (!slaDuration || slaDuration < 1)) {
      setValidationError('Please enter a valid SLA duration (minimum 1)');
      return false;
    }
    
    // Validate operator for assign_operator action
    if (actionType === 'assign_operator' && assignmentType === 'specific' && !selectedOperator) {
      setValidationError('Please select an operator for the assignment');
      return false;
    }
    
    setValidationError('');
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    // Build action config based on action type
    let actionConfigObj = {};
    if (actionType === 'send_message') {
      actionConfigObj = { message: actionConfig };
    } else if (actionType === 'assign_operator') {
      actionConfigObj = { 
        assignment_type: assignmentType,
        operator_id: assignmentType === 'specific' ? selectedOperator : null
      };
    } else if (actionType === 'call_ai') {
      actionConfigObj = { auto_send: false };
    } else {
      actionConfigObj = { message: actionConfig };
    }
    
    // Build trigger config
    let triggerConfig = {};
    if (triggerType === 'intent_detected') {
      triggerConfig = { intent: triggerIntent };
    } else if (triggerType === 'no_response_sla' || triggerType === 'time_based') {
      // SLA triggers require duration configuration
      triggerConfig = { 
        sla_duration: parseInt(slaDuration, 10),
        sla_unit: slaUnit
      };
    }
    
    const data = {
      name,
      description,
      trigger: { type: triggerType, config: triggerConfig },
      conditions: [],
      actions: [{ type: actionType, config: actionConfigObj }]
    };
    
    try {
      if (editingWorkflow) {
        await axios.put(`${API}/workflows/${editingWorkflow.id}`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Workflow updated');
      } else {
        const url = selectedChannelId && selectedChannelId !== 'all'
          ? `${API}/channels/${selectedChannelId}/workflows`
          : `${API}/workflows`;
        await axios.post(url, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Workflow created');
      }
      
      setIsDialogOpen(false);
      resetForm();
      fetchWorkflows();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to save workflow';
      toast.error(errorMsg);
      setValidationError(errorMsg);
    }
  };

  const handleDelete = async (workflowId) => {
    if (!window.confirm('Are you sure you want to delete this workflow?')) return;
    
    try {
      await axios.delete(`${API}/workflows/${workflowId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Workflow deleted');
      fetchWorkflows();
    } catch (error) {
      toast.error('Failed to delete workflow');
    }
  };

  const toggleActive = async (workflow) => {
    try {
      await axios.put(`${API}/workflows/${workflow.id}`, { is_active: !workflow.is_active }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchWorkflows();
      toast.success(`Workflow ${!workflow.is_active ? 'activated' : 'paused'}`);
    } catch (error) {
      toast.error('Failed to update workflow');
    }
  };

  const getTriggerIcon = (type) => {
    switch (type) {
      case 'new_message': return MessageSquare;
      case 'intent_detected': return Zap;
      case 'time_based': return Clock;
      default: return WorkflowIcon;
    }
  };

  const getActionIcon = (type) => {
    switch (type) {
      case 'send_message': return MessageSquare;
      case 'assign_operator': return UserPlus;
      case 'send_email': return Mail;
      case 'call_ai': return Zap;
      default: return WorkflowIcon;
    }
  };

  const getTriggerLabel = (workflow) => {
    const type = workflow.trigger?.type;
    if (type === 'intent_detected') {
      const intent = workflow.trigger?.config?.intent;
      return intent ? `Intent: ${intent}` : 'Intent Detected';
    }
    return type?.replace('_', ' ');
  };

  const getActionLabel = (workflow) => {
    const action = workflow.actions?.[0];
    if (!action) return 'No action';
    
    if (action.type === 'assign_operator') {
      const assignType = action.config?.assignment_type;
      if (assignType === 'owner') return 'Assign to Owner';
      if (assignType === 'admin') return 'Assign to Admin';
      return 'Assign Operator';
    }
    return action.type?.replace('_', ' ');
  };

  return (
    <div className="space-y-6" data-testid="workflows-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Workflow Automation</h1>
          <p className="text-muted-foreground">Build automated workflows to handle common scenarios</p>
          <div className="mt-2" data-testid="workflow-channel-selector">
            <ChannelSelector
              value={selectedChannelId}
              onChange={(channelId) => setSelectedChannelId(channelId)}
              showAllOption={user?.role === 'owner' || user?.is_super_admin}
            />
          </div>
        </div>
        
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={openCreateDialog} className="bg-emerald-700 hover:bg-emerald-800" data-testid="add-workflow-btn">
              <Plus className="w-4 h-4 mr-2" />
              Create Workflow
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>{editingWorkflow ? 'Edit Workflow' : 'Create New Workflow'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              {validationError && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{validationError}</AlertDescription>
                </Alert>
              )}
              
              <div className="space-y-2">
                <Label htmlFor="name">Workflow Name</Label>
                <Input
                  id="name"
                  placeholder="e.g., Auto-reply to inquiries"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  data-testid="workflow-name-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  placeholder="Describe what this workflow does..."
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={2}
                  data-testid="workflow-description-input"
                />
              </div>
              
              {/* Trigger Section */}
              <div className="space-y-4 p-4 border rounded-lg bg-muted/30">
                <Label className="text-base font-semibold">Trigger</Label>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Trigger Type</Label>
                    <Select value={triggerType} onValueChange={(v) => { setTriggerType(v); setValidationError(''); }}>
                      <SelectTrigger data-testid="workflow-trigger-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="new_message">New Message Received</SelectItem>
                        <SelectItem value="intent_detected">Intent Detected</SelectItem>
                        <SelectItem value="time_based">Time-based (SLA)</SelectItem>
                        <SelectItem value="payment_event">Payment Event</SelectItem>
                        <SelectItem value="no_response_sla">No Response SLA</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* Intent selector - only show for intent_detected trigger */}
                  {triggerType === 'intent_detected' && (
                    <div className="space-y-2">
                      <Label>
                        Intent <span className="text-red-500">*</span>
                      </Label>
                      <Select value={triggerIntent} onValueChange={(v) => { setTriggerIntent(v); setValidationError(''); }}>
                        <SelectTrigger data-testid="workflow-intent-select">
                          <SelectValue placeholder="Select intent..." />
                        </SelectTrigger>
                        <SelectContent>
                          {INTENTS.map(intent => (
                            <SelectItem key={intent.value} value={intent.value}>
                              {intent.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-muted-foreground">
                        Workflow triggers only when this intent is detected
                      </p>
                    </div>
                  )}
                  
                  {/* SLA Duration - only show for SLA triggers */}
                  {(triggerType === 'no_response_sla' || triggerType === 'time_based') && (
                    <>
                      <div className="col-span-2 grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>
                            SLA Duration <span className="text-red-500">*</span>
                          </Label>
                          <Input
                            type="number"
                            min="1"
                            value={slaDuration}
                            onChange={(e) => { setSlaDuration(e.target.value); setValidationError(''); }}
                            placeholder="5"
                            data-testid="workflow-sla-duration-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Time Unit</Label>
                          <Select value={slaUnit} onValueChange={setSlaUnit}>
                            <SelectTrigger data-testid="workflow-sla-unit-select">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="seconds">Seconds</SelectItem>
                              <SelectItem value="minutes">Minutes</SelectItem>
                              <SelectItem value="hours">Hours</SelectItem>
                              <SelectItem value="days">Days</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div className="col-span-2">
                        <Alert className="border-blue-200 bg-blue-50 dark:bg-blue-900/20">
                          <Clock className="h-4 w-4 text-blue-600" />
                          <AlertDescription className="text-blue-800 dark:text-blue-200 text-sm">
                            <strong>SLA Workflow:</strong> Timer starts after the last customer message. If no response is sent within the specified duration, the workflow action will execute automatically. SLA messages are always sent immediately (HITL does not apply).
                          </AlertDescription>
                        </Alert>
                      </div>
                    </>
                  )}
                </div>
              </div>
              
              {/* Action Section */}
              <div className="space-y-4 p-4 border rounded-lg bg-muted/30">
                <Label className="text-base font-semibold">Action</Label>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Action Type</Label>
                    <Select value={actionType} onValueChange={(v) => { setActionType(v); setValidationError(''); }}>
                      <SelectTrigger data-testid="workflow-action-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="send_message">Send WhatsApp Message</SelectItem>
                        <SelectItem value="assign_operator">Assign to Operator</SelectItem>
                        <SelectItem value="call_ai">Get AI Response</SelectItem>
                        <SelectItem value="send_email">Send Email</SelectItem>
                        <SelectItem value="trigger_payment">Trigger Payment Link</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* Message input for send_message action */}
                  {actionType === 'send_message' && (
                    <div className="space-y-2">
                      <Label htmlFor="actionConfig">Message Content</Label>
                      <Textarea
                        id="actionConfig"
                        placeholder="Enter the auto-reply message..."
                        value={actionConfig}
                        onChange={(e) => setActionConfig(e.target.value)}
                        rows={3}
                        data-testid="workflow-action-config"
                      />
                    </div>
                  )}
                  
                  {/* Operator selector for assign_operator action */}
                  {actionType === 'assign_operator' && (
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label>Assignment Target</Label>
                        <Select value={assignmentType} onValueChange={(v) => { setAssignmentType(v); setValidationError(''); }}>
                          <SelectTrigger data-testid="workflow-assignment-type">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {ASSIGNMENT_TYPES.map(type => (
                              <SelectItem key={type.value} value={type.value}>
                                {type.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      
                      {assignmentType === 'specific' && (
                        <div className="space-y-2">
                          <Label>
                            Select Operator <span className="text-red-500">*</span>
                          </Label>
                          <Select value={selectedOperator} onValueChange={(v) => { setSelectedOperator(v); setValidationError(''); }}>
                            <SelectTrigger data-testid="workflow-operator-select">
                              <SelectValue placeholder="Select operator..." />
                            </SelectTrigger>
                            <SelectContent>
                              {operators.map(op => (
                                <SelectItem key={op.id} value={op.id}>
                                  {op.name} ({op.role})
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Info for call_ai action */}
                  {actionType === 'call_ai' && (
                    <div className="p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg text-sm">
                      <p className="text-amber-800 dark:text-amber-200">
                        <strong>Note:</strong> AI responses respect your AI Settings:
                      </p>
                      <ul className="list-disc ml-5 mt-1 text-amber-700 dark:text-amber-300">
                        <li>If AI is disabled, this action will be skipped</li>
                        <li>If Human-in-Loop is ON, AI replies will be saved as drafts</li>
                        <li>If Human-in-Loop is OFF, AI replies will be auto-sent</li>
                      </ul>
                    </div>
                  )}
                </div>
              </div>
              
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-emerald-700 hover:bg-emerald-800" data-testid="save-workflow-btn">
                  {editingWorkflow ? 'Update Workflow' : 'Create Workflow'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
      
      {/* Workflow Canvas Preview */}
      <div className="workflow-canvas rounded-xl border p-8 min-h-[200px]">
        <div className="flex items-center justify-center h-full text-muted-foreground">
          <div className="text-center">
            <WorkflowIcon className="w-12 h-12 mx-auto mb-4 opacity-30" />
            <p className="text-sm">Visual workflow builder coming soon</p>
            <p className="text-xs mt-1">For now, create workflows using the form above</p>
          </div>
        </div>
      </div>
      
      {/* Workflow List */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {workflows.length === 0 ? (
          <div className="col-span-full text-center py-12 bg-card rounded-xl border">
            <WorkflowIcon className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
            <p className="text-muted-foreground">No workflows yet</p>
            <p className="text-sm text-muted-foreground mt-1">Create your first automation workflow</p>
          </div>
        ) : (
          workflows.map(workflow => {
            const TriggerIcon = getTriggerIcon(workflow.trigger?.type);
            const ActionIcon = getActionIcon(workflow.actions?.[0]?.type);
            
            return (
              <Card key={workflow.id} className={!workflow.is_active ? 'opacity-60' : ''} data-testid={`workflow-item-${workflow.id}`}>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg">{workflow.name}</CardTitle>
                    <Switch
                      checked={workflow.is_active}
                      onCheckedChange={() => toggleActive(workflow)}
                      data-testid={`toggle-workflow-${workflow.id}`}
                    />
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
                    {workflow.description || 'No description'}
                  </p>
                  
                  <div className="flex items-center gap-2 mb-4 flex-wrap">
                    <div className="flex items-center gap-1 px-2 py-1 bg-muted rounded text-xs">
                      <TriggerIcon className="w-3 h-3" />
                      {getTriggerLabel(workflow)}
                    </div>
                    <span className="text-muted-foreground">→</span>
                    <div className="flex items-center gap-1 px-2 py-1 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 rounded text-xs">
                      <ActionIcon className="w-3 h-3" />
                      {getActionLabel(workflow)}
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <Badge variant={workflow.is_active ? 'default' : 'secondary'} className={workflow.is_active ? 'bg-emerald-600' : ''}>
                      {workflow.is_active ? 'Active' : 'Paused'}
                    </Badge>
                    <div className="flex gap-1">
                      <Button 
                        variant="ghost" 
                        size="icon"
                        onClick={() => openEditDialog(workflow)}
                        data-testid={`edit-workflow-${workflow.id}`}
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="icon"
                        onClick={() => handleDelete(workflow.id)}
                        className="text-red-500 hover:text-red-600"
                        data-testid={`delete-workflow-${workflow.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>
    </div>
  );
};

export default WorkflowsPage;
